import { isImage } from "@/helpers/isImg"
import { isVideo } from "@/helpers/isVideo"
import { FilterSearch } from "@/models/Filter"
import { formatDateAgo, parseDate } from "@/utils/dateUtil"

import { ResourceType } from "./common"
import { User } from "./User"

export interface UploadAttachment {
  attachments: Blob[]
  resourceType?: ResourceType
  projectId?: string
  /** Controls whether file requests include the current project context. */
  projectScoped?: boolean
  targetId?: string
  onUploadProgress?: any
  excludeDuplicate?: boolean
  onlySend?: boolean
  /** Files tab that shows the upload: its placeholders and progress are kept in the file store under this key. */
  uploadListKey?: string
}

export type UploadQueryFile = {
  onUploadProgress?: any
  signal?: AbortSignal
  params?: {
    excludeDuplicate?: boolean
    projectId?: string
  }
}

/** Where a part-by-part upload is attached on completion; no resourceType and targetId means attached later. */
export interface DirectUploadTarget {
  projectId?: string
  resourceType?: ResourceType
  targetId?: string
  demo?: boolean
  excludeDuplicate?: boolean
}

export interface DirectUploadStart {
  uploadToken: string
  partSize: number
  partCount: number
}

export interface DirectUploadPart {
  partNumber: number
  etag: string
}

export enum FileType {
  IMAGE = "IMAGE",
  VIDEO = "VIDEO",
  FILE = "FILE"
}

export type UploadAttachmentFn = (args: UploadAttachment) => Promise<FileMetaData[]>

export interface AttachmentFileMetadataParams {
  resourceType: ResourceType
  targetId: string
  projectId?: string
  /** Controls whether file metadata requests include the current project context. */
  projectScoped?: boolean
  filter: FilterSearch
}

export type FileDownloadStatus = "preparing" | "started"
/** File row markup the download was started from: the spinner is shown only where the user clicked. */
export type FileDownloadSource = "line" | "element" | "simplified"
export type FileDownloadStatusesBySource = Partial<Record<FileDownloadSource, FileDownloadStatus>>

export type TypeConfig = {
  type: ResourceType
  targetId: string
}

export class VUFile {
  file: File = new File([], "")
  readonly fileObject = false
  id: string | number = ""
  size = 0
  name = ""
  type = ""
  custom = false
  active = false
  error = ""
  success = false
  putAction = false
  postAction = false
  headers: Record<string, unknown> = {}
  data: Record<string, unknown> = {}
  timeout = 0
  response: Record<string, unknown> | string = ""
  progress = ""
  speed = 0
  xhr: XMLHttpRequest = new XMLHttpRequest()
  iframe?: Element

  constructor(props?: Partial<VUFile>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export interface IFile {
  id: string
  owner: User
  name: string
  size: number
  createdDate: string
  targetType: string
  custom?: boolean
  pendingFile?: File
  deletedBy?: User
  deletedAt?: Date
  deleted?: boolean
  demo?: boolean
}

export class FileMetaData implements IFile {
  id: string
  owner: User
  name: string
  size: number
  createdDate: string
  targetType: string
  custom?: boolean
  pendingFile?: File
  deletedBy?: User
  deletedAt?: Date
  deleted?: boolean
  demo?: boolean

  constructor(props?: IFile) {
    if (!props) return

    this.set(props)
  }

  set(props: IFile) {
    this.id = props.id ?? ""
    this.owner = User.create(props.owner)
    this.deletedBy = props.deletedBy ? User.create(props.deletedBy) : undefined
    this.deletedAt = props.deletedAt ? new Date(props.deletedAt) : new Date()
    this.name = props.name ?? ""
    this.size = props.size ?? 0
    this.createdDate = props.createdDate ?? ""
    this.targetType = props.targetType ?? ""
    this.custom = props.custom
    this.pendingFile = props.pendingFile
    this.deleted = props.deleted ?? false
    this.demo = props.demo ?? false
  }

  get createdDateParsed() {
    return parseDate(this.createdDate, true)
  }

  get deletedTimeAgo() {
    if (!this.deletedAt) return ""
    return formatDateAgo({
      date: new Date(),
      dateFormat: this.deletedAt,
      short: true,
      sufixAgo: true
    })
  }

  get ext() {
    return this.name.split(".").pop() ?? ""
  }

  get isImage() {
    return isImage(this.name)
  }

  get isVideo() {
    return isVideo(this.name)
  }

  get fileType(): FileType {
    if (this.isImage) {
      return FileType.IMAGE
    }
    if (this.isVideo) {
      return FileType.VIDEO
    }
    return FileType.FILE
  }

  getJsonObj() {
    return {}
  }

  static create(props?: IFile): FileMetaData {
    return new FileMetaData(props)
  }
}
